<template>
  <v-card
    class="pipeline-board d-flex flex-column"
    flat
    :disabled="isProcessing"
    :loading="isProcessing"
  >
    <template v-slot:progress>
      <v-progress-linear
        absolute
        color="success"
        height="4"
        indeterminate
      ></v-progress-linear>
    </template>

    <div v-if="orderedStages.length" class="pipeline-stages-scroll">
      <div
        v-for="stage in orderedStages"
        :key="stage.id"
        class="pipeline-stage-col"
      >
        <v-card outlined class="stage-column d-flex flex-column">
          <v-card-title class="stage-column__title py-3 primary white--text flex-grow-0">
            <span class="subtitle-1 font-weight-medium">{{ stage.name }}</span>
          </v-card-title>
          <v-card-text class="stage-column__body d-flex flex-column">
            <div
              class="stage-deals-scroll"
              :class="{ 'stage-deals-scroll--empty': !stage.deals.length }"
            >
              <div
                class="stage-deals-stack"
                :class="{ 'stage-deals-stack--empty': !stage.deals.length }"
              >
                <Draggable
                  v-model="stage.deals"
                  tag="div"
                  :group="dealDragGroup"
                  :disabled="!canReorderDeals || reorderingDeals"
                  :animation="200"
                  :delay-on-touch-only="true"
                  :delay="180"
                  :touch-start-threshold="6"
                  ghost-class="deal-ghost"
                  chosen-class="deal-chosen"
                  drag-class="deal-drag"
                  class="deal-list-draggable"
                  @end="onDealDragEnd"
                >
                  <div
                    v-for="deal in stage.deals"
                    :key="'deal-' + deal.id"
                    class="deal-card"
                    :class="{
                      'deal-card--draggable': canReorderDeals && !reorderingDeals,
                      'deal-card--organization-bg': hasBgOrganization(deal),
                    }"
                    @click="openDealViewModal(deal)"
                  >
                    <div class="deal-card__title text-body-2 font-weight-medium">
                      {{ deal.name || $t("DEALS.MODAL.DEAL_FALLBACK_TITLE", { id: deal.id }) }}
                    </div>
                    <div class="deal-card__code text-caption text--secondary mt-1">
                      {{ formatDealCode(deal) }}
                    </div>
                    <div
                      v-if="deal.deal_type?.name || deal.deal_status?.name"
                      class="deal-card__meta mt-2"
                    >
                      <v-chip
                        v-if="deal.deal_type?.name"
                        x-small
                        outlined
                        :color="getDealTypeChipColor(deal.deal_type)"
                        class="deal-card__chip mr-1 mb-0"
                      >
                        {{ deal.deal_type.name }}
                      </v-chip>
                      <v-chip
                        v-if="deal.deal_status?.name"
                        x-small
                        outlined
                        color="primary"
                        class="deal-card__chip mb-0"
                      >
                        {{ deal.deal_status.name }}
                      </v-chip>
                    </div>
                  </div>
                </Draggable>
                <div
                  v-if="!stage.deals.length"
                  class="stage-column__empty text--secondary body-1 font-weight-medium"
                >
                  {{ $t("DEALS.MODAL.NO_DEALS") }}
                </div>
              </div>
            </div>
            <v-btn
              block
              small
              color="primary"
              class="stage-column__add-btn flex-shrink-0"
              :disabled="!hasPermissions('deals-create')"
              @click="openCreateDealModal(stage)"
            >
              {{ $t("DEALS.MODAL.ADD_DEAL_BUTTON") }}
            </v-btn>
          </v-card-text>
        </v-card>
      </div>
    </div>
    <div v-else-if="!isProcessing" class="text--secondary pa-4">
      {{ $t("PIPELINES.NO_STAGES_IN_PIPELINE") }}
    </div>

    <deal-modal
      :open="dealModalOpen"
      :update-deal-id="updateDealId"
      :pipeline-stage-id="pipelineStageId"
      @close="closeDealModal"
      @saved="onDealSaved"
      @updated="onDealUpdated"
    />
  </v-card>
</template>

<script>
import Draggable from "vuedraggable";
import DealModal from "@/views/pages/deals/partials/DealModal";
import { GET_PIPELINE, REORDER_PIPELINE_DEALS } from "@/store/pipelines.module";
import { SHOW_SNACK } from "@/store/snack.module";
import DealsService from "@/common/deals.service";

export default {
  name: "PipelineBoard",
  components: {
    Draggable,
    DealModal,
  },
  props: {
    pipelineId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      isProcessing: false,
      stages: [],
      reorderingDeals: false,
      dealModalOpen: false,
      updateDealId: null,
      pipelineStageId: null,
    };
  },
  computed: {
    orderedStages() {
      return [...this.stages].sort(
        (a, b) => Number(a.order || 0) - Number(b.order || 0)
      );
    },
    canReorderDeals() {
      const seesAllDeals = this.$store.getters.currentUser?.sees_all_deals;

      return this.hasPermissions("deals-edit")
        && this.hasPermissions("deals-view-all")
        && seesAllDeals !== false;
    },
    dealDragGroup() {
      return {
        name: `pipeline-deals-${this.pipelineId}`,
        pull: this.canReorderDeals,
        put: this.canReorderDeals,
      };
    },
  },
  mounted() {
    this.loadPipeline();
  },
  watch: {
    pipelineId() {
      this.loadPipeline();
    },
  },
  methods: {
    formatDealCode(deal) {
      return DealsService.formatDealCode(deal);
    },
    getDealTypeChipColor(dealType) {
      return DealsService.getDealTypeChipColor(dealType);
    },
    hasBgOrganization(deal) {
      return deal?.organization?.code === "BG";
    },
    loadPipeline() {
      this.isProcessing = true;
      this.$store
        .dispatch(GET_PIPELINE, this.pipelineId)
        .then((response) => {
          const name = response.name || "";
          const stages = Array.isArray(response.stages) ? response.stages : [];

          stages.forEach((stage) => {
            stage.deals = this.unwrapDealsList(stage.deals);
            stage.deals.sort(
              (a, b) =>
                Number(a.order || 0) - Number(b.order || 0) ||
                Number(a.id || 0) - Number(b.id || 0)
            );
          });

          this.stages = stages;
          this.showDealsPerformanceWarningIfNeeded(stages);
          this.$emit("pipeline-loaded", { name });
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
        })
        .finally(() => {
          this.isProcessing = false;
        });
    },
    countPipelineBoardDeals(stages) {
      return (Array.isArray(stages) ? stages : []).reduce(
        (total, stage) => total + this.unwrapDealsList(stage.deals).length,
        0
      );
    },
    showDealsPerformanceWarningIfNeeded(stages) {
      const dealsCount = this.countPipelineBoardDeals(stages);
      if (dealsCount <= 300) {
        return;
      }

      this.$store.dispatch(SHOW_SNACK, {
        message: this.$t("PIPELINES.TOO_MANY_DEALS_WARNING", { count: dealsCount }),
        type: "warning",
      });
    },
    unwrapDealsList(raw) {
      if (raw && typeof raw === "object" && !Array.isArray(raw) && Array.isArray(raw.data)) {
        return raw.data;
      }

      return Array.isArray(raw) ? raw : [];
    },
    openCreateDealModal(stage) {
      this.updateDealId = null;
      this.pipelineStageId = stage?.id || null;
      this.dealModalOpen = true;
    },
    openDealViewModal(deal) {
      if (!deal?.id) {
        return;
      }

      this.pipelineStageId = null;
      this.updateDealId = deal.id;
      this.dealModalOpen = true;
    },
    closeDealModal() {
      this.dealModalOpen = false;
      this.updateDealId = null;
      this.pipelineStageId = null;
    },
    onDealSaved() {
      this.loadPipeline();
    },
    onDealUpdated() {
      this.loadPipeline();
    },
    buildReorderStagesPayload() {
      return this.orderedStages.map((stage) => {
        const dealIds = this.unwrapDealsList(stage.deals)
          .map((d) => parseInt(d && d.id, 10))
          .filter((id) => Number.isFinite(id) && id > 0);

        return {
          id: parseInt(stage.id, 10),
          deal_ids: dealIds,
        };
      });
    },
    onDealDragEnd() {
      if (!this.canReorderDeals) {
        return;
      }

      const stages = this.buildReorderStagesPayload();
      this.reorderingDeals = true;

      this.$store
        .dispatch(REORDER_PIPELINE_DEALS, {
          pipelineId: this.pipelineId,
          stages,
        })
        .catch(() => {
          this.$store.dispatch(SHOW_SNACK, {
            message: this.$t("GENERAL.UNEXPECTED_ERROR"),
            type: "error",
          });
          this.loadPipeline();
        })
        .finally(() => {
          this.reorderingDeals = false;
        });
    },
  },
};
</script>

<style scoped>
.pipeline-board {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background-color: #fff !important;
}

.pipeline-stages-scroll {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 4px;
  margin-right: -12px;
  padding-right: 12px;
}

.pipeline-stage-col {
  flex: 1 0 250px;
  min-width: 250px;
  max-width: 500px;
  max-height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-self: flex-start;
}

.stage-column__title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.stage-column {
  width: 100%;
  max-height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff !important;
}

.stage-column__body {
  flex: 1 1 auto;
  min-height: 0 !important;
  display: flex;
  flex-direction: column;
  overflow: hidden !important;
  padding: 14px 12px 12px !important;
  background-color: #fff !important;
}

.stage-deals-scroll {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.stage-deals-scroll--empty {
  min-height: 72px;
}

.stage-deals-stack {
  position: relative;
  overflow-x: hidden;
}

.stage-deals-stack--empty .deal-list-draggable {
  min-height: 52px;
  margin-bottom: 0;
}

.deal-list-draggable {
  min-height: 24px;
  margin-bottom: 2px;
}

.stage-column__add-btn {
  margin-top: 12px !important;
}

.deal-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.deal-card {
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 10px;
  box-sizing: border-box;
  background-color: #fff;
  border: 1px solid var(--v-primary-base) !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.15s ease, transform 0.15s ease;
}

.deal-card.deal-card--organization-bg {
  background-color: #e8f5e9;
  border-color: var(--v-success-base) !important;
}

.deal-card--draggable {
  cursor: pointer;
  user-select: none;
}

.deal-card--draggable:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.deal-card.deal-chosen {
  opacity: 0.97;
}

.deal-card.deal-ghost {
  opacity: 0.75;
  background: rgba(0, 0, 0, 0.04) !important;
  border-radius: 10px;
  border: 1px dashed var(--v-primary-base) !important;
  box-shadow: none;
  cursor: grabbing !important;
}

.deal-card.deal-drag {
  opacity: 0.92;
  border: 1px solid var(--v-primary-base) !important;
  cursor: grabbing !important;
}

.stage-column__empty {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  padding: 0 8px;
  line-height: 1.4;
  pointer-events: none;
}
</style>
